"use client";

/**
 * Site Control — pricing-page card for the per-site enterprise tier.
 *
 * Mounted below <PricingCards> + <SetupServicePricingCard> in
 * app/(marketing)/pricing/page.tsx. Gated on useAppFeatureFlag("site_control"),
 * which is now a GA KILL-SWITCH (fail-open via isSiteControlLive on the server):
 * the card is LIVE unless an operator explicitly disables the tier, and renders
 * nothing (no skeleton, no reflow) only when it is killed.
 *
 * The CTA is "Book a walkthrough" (a Link to /site-control, the dedicated
 * landing route repointed in LAND-07), NOT a Stripe checkout, because Site
 * Control is quote-led (per-site + setup) and has no self-serve price. Copy is
 * sourced from lib/site-control-copy.ts.
 */

import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import {
  SITE_CONTROL_NAME,
  SITE_CONTROL_TAGLINE,
  SITE_CONTROL_PRICE_DISPLAY,
  SITE_CONTROL_PRICE_PERIOD,
  SITE_CONTROL_SETUP_DISPLAY,
  SITE_CONTROL_FEATURES,
  SITE_CONTROL_CTA,
  SITE_CONTROL_SECTION_EYEBROW,
  SITE_CONTROL_SECTION_HEADING,
} from "@/lib/site-control-copy";

export function SiteControlPricingCard() {
  const flagEnabled = useAppFeatureFlag("site_control");

  // Off (or undefined) → render nothing.
  if (flagEnabled !== true) return null;

  return (
    <div
      id="site-control"
      data-testid="site-control-pricing"
      className="mx-auto mt-12 max-w-3xl scroll-mt-24 text-left lg:mt-16"
    >
      {/* Section-break header — frames Site Control as a distinct enterprise
          product class above the self-serve grid, not a fourth self-serve tier.
          Mirrors the page's eyebrow/hairline pattern (the "Business plan"
          header block) but in the navy enterprise key. */}
      <div className="mb-6 text-center">
        <p className="flex items-center justify-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-text-secondary">
          <span aria-hidden className="h-px w-8 bg-accent/60" />
          <span className="text-navy">{SITE_CONTROL_SECTION_EYEBROW}</span>
          <span aria-hidden className="h-px w-8 bg-accent/60" />
        </p>
        <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
          {SITE_CONTROL_SECTION_HEADING}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted">
          {SITE_CONTROL_TAGLINE}
        </p>
      </div>

      <div className="rounded-2xl border border-navy/30 bg-surface p-8 shadow-lg ring-1 ring-navy/10">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-lg font-bold text-navy">
            {SITE_CONTROL_NAME}
          </h3>
          <span className="rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white">
            Enterprise
          </span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          {SITE_CONTROL_TAGLINE}
        </p>

        <p className="mt-4">
          <span className="text-4xl font-extrabold text-navy">
            {SITE_CONTROL_PRICE_DISPLAY}
          </span>
          <span className="ml-2 text-sm text-muted">
            {SITE_CONTROL_PRICE_PERIOD}
          </span>
        </p>
        <p className="mt-2">
          <span className="inline-block rounded-md bg-done-bg px-2 py-1 text-xs font-medium text-done">
            plus {SITE_CONTROL_SETUP_DISPLAY} one-off setup
          </span>
        </p>

        <ul className="mt-6 space-y-3">
          {SITE_CONTROL_FEATURES.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <CheckCircle2
                className="mt-0.5 h-4 w-4 shrink-0 text-navy"
                aria-hidden="true"
              />
              <span className="text-sm text-muted">{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <Link
            href="/site-control"
            className="block min-h-[44px] w-full rounded-lg bg-navy px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-navy/90"
          >
            {SITE_CONTROL_CTA}
          </Link>
          <p className="mt-2 text-center text-xs text-muted">
            Per active site, unlimited workers. Volume pricing for multiple sites.
          </p>
        </div>
      </div>
    </div>
  );
}
